import { useState } from "react"
import type { AmortizationEntry } from "@/utils/emiCalculations"
import { formatCurrencyFull } from "@/utils/emiCalculations"

interface AmortizationTableProps {
  schedule: AmortizationEntry[]
}

export default function AmortizationTable({ schedule }: AmortizationTableProps) {
  const [viewMode, setViewMode] = useState<"monthly" | "yearly">("yearly")

  const displayData = viewMode === "yearly"
    ? schedule.filter((_, i) => (i + 1) % 12 === 0 || i === schedule.length - 1)
    : schedule

  return (
    <div className="mt-8 bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
      <div className="p-4 md:p-6 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h3 className="text-lg font-bold text-slate-800">Amortization Schedule</h3>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode("yearly")}
            className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              viewMode === "yearly"
                ? "bg-blue-700 text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            Yearly
          </button>
          <button
            onClick={() => setViewMode("monthly")}
            className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              viewMode === "monthly"
                ? "bg-blue-700 text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            Monthly
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" role="table" aria-label="Amortization Schedule">
          <thead>
            <tr className="bg-slate-50">
              <th className="text-left px-4 md:px-6 py-3 font-semibold text-slate-600">
                {viewMode === "yearly" ? "Year" : "Month"}
              </th>
              <th className="text-right px-4 md:px-6 py-3 font-semibold text-slate-600">EMI Paid</th>
              <th className="text-right px-4 md:px-6 py-3 font-semibold text-slate-600">Principal</th>
              <th className="text-right px-4 md:px-6 py-3 font-semibold text-slate-600">Interest</th>
              <th className="text-right px-4 md:px-6 py-3 font-semibold text-slate-600">Balance</th>
            </tr>
          </thead>
          <tbody>
            {displayData.map((entry) => (
              <tr key={entry.month} className="border-t border-slate-50 hover:bg-slate-50 transition-colors">
                <td className="px-4 md:px-6 py-3 text-slate-700 font-medium">
                  {viewMode === "yearly" ? Math.ceil(entry.month / 12) : entry.month}
                </td>
                <td className="px-4 md:px-6 py-3 text-right text-slate-700">{formatCurrencyFull(entry.emiPaid)}</td>
                <td className="px-4 md:px-6 py-3 text-right text-blue-700 font-medium">{formatCurrencyFull(entry.principalPaid)}</td>
                <td className="px-4 md:px-6 py-3 text-right text-slate-500">{formatCurrencyFull(entry.interestPaid)}</td>
                <td className="px-4 md:px-6 py-3 text-right text-slate-700 font-medium">{formatCurrencyFull(entry.remainingBalance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
